import { ReactComponent as ConfirmIcon } from 'assets/staking/modal-confirm.svg';
import { ReactComponent as ErrorIcon } from 'assets/staking/modal-error.svg';
import { ReactComponent as SuccessIcon } from 'assets/staking/modal-success.svg';

import { EarningsFilterType, EarningsTableStatus, PopupConfig, StakingStatusVariant, StakingTab } from './types';

// Data processing constants
export const DEFAULT_TOKEN_DECIMALS = 18;
export const FINAL_CALL_THRESHOLD = 0.2; // 20%
export const ITEMS_PER_PAGE = 10;
export const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
export const ONE_ADDRESS = '0x0000000000000000000000000000000000000001';

/**
 * Maps period days value to display label
 */
export const PERIOD_LABELS: Record<string, string> = {
  '0': 'Flexible',
  '1': '1 Day',
  '2': '2 Days',
  '3': '3 Days',
  '4': '4 Days',
  '7': '7 Days',
  '30': '30 Days',
  '90': '90 Days',
  '180': '180 Days',
  '360': '360 Days',
  '720': '720 Days',
};

// UI constants
export const stakingTabs = [
  { title: 'Stake', type: StakingTab.STAKE },
  { title: 'VIP', type: StakingTab.VIP, colorVariant: 'orange' as const },
  { title: 'Earnings', type: StakingTab.EARNINGS },
  { title: 'Games Pool', type: StakingTab.GAMES_POOL },
];


export const STAKING_PERIODS = [
  { name: 'Flexible', days: 0 },
  { name: '7 Days', days: 7 },
  { name: '30 Days', days: 30 },
  { name: '90 Days', days: 90 },
  { name: '180 Days', days: 180 },
] as const;

export type StakingPeriod = (typeof STAKING_PERIODS)[number];

export const EARNINGS_COLUMNS = ['Phase', 'Status', 'Staked Amount', 'APY %', 'Reward', 'Actions'];
export const EARNINGS_COLUMNS_MOBILE = ['All', 'Status', 'Staked Amount'];

export const EARNINGS_SORT_CONFIG = [
  {
    index: 0,
    key: 'phase',
  },
  {
    index: 2,
    key: 'stakedAmount',
  },
  {
    index: 3,
    key: 'apy',
  },
  {
    index: 4,
    key: 'reward',
  },
];

// Popup variants configuration
export const POPUP_VARIANTS: Record<StakingStatusVariant, PopupConfig> = {
  confirm: {
    Icon: ConfirmIcon,
    title: 'Confirm Stake Token',
    description: 'Please confirm this transaction in your wallet to proceed with staking.',
    buttonText: 'Waiting for approval',
    isButtonDisabled: true,
    titleColor: 'tw-text-[#2D5EFF]',
  },
  success: {
    Icon: SuccessIcon,
    title: 'Transaction Submitted',
    description: 'Your staking request has been submitted. Your balance will update shortly.',
    buttonText: 'View Transaction',
    isButtonDisabled: false,
    titleColor: 'tw-text-[#20AA67]',
  },
  error: {
    Icon: ErrorIcon,
    title: 'Transaction Declined',
    description: 'This transaction was declined in your wallet. Please try again.',
    buttonText: 'Retry',
    isButtonDisabled: false,
    titleColor: 'tw-text-[#F83A56]',
  },
  'claim-confirm': {
    Icon: ConfirmIcon,
    title: 'Confirm Claim',
    description: 'Please confirm this transaction in your wallet to claim your rewards.',
    buttonText: 'Waiting for approval',
    isButtonDisabled: true,
    titleColor: 'tw-text-[#2D5EFF]',
  },
  'claim-success': {
    Icon: SuccessIcon,
    title: 'Claim Submitted',
    description: 'Your claim request has been submitted. Your balance will update shortly.',
    buttonText: 'View Transaction',
    isButtonDisabled: false,
    titleColor: 'tw-text-[#20AA67]',
  },
  'claim-error': {
    Icon: ErrorIcon,
    title: 'Claim Declined',
    description: 'This transaction was declined in your wallet. Please try again.',
    buttonText: 'Retry',
    isButtonDisabled: false,
    titleColor: 'tw-text-[#F83A56]',
  },
  'withdraw-confirm': {
    Icon: ConfirmIcon,
    title: 'Confirm Withdrawal',
    description: 'Please confirm this transaction in your wallet to withdraw your tokens.',
    buttonText: 'Waiting for approval',
    isButtonDisabled: true,
    titleColor: 'tw-text-[#2D5EFF]',
  },
  'withdraw-success': {
    Icon: SuccessIcon,
    title: 'Withdrawal Submitted',
    description: 'Your withdrawal request has been submitted. Your balance will update shortly.',
    buttonText: 'View Transaction',
    isButtonDisabled: false,
    titleColor: 'tw-text-[#20AA67]',
  },
  'withdraw-error': {
    Icon: ErrorIcon,
    title: 'Withdrawal Declined',
    description: 'This transaction was declined in your wallet. Please try again.',
    buttonText: 'Retry',
    isButtonDisabled: false,
    titleColor: 'tw-text-[#F83A56]',
  },
};

export const FILTERS = [
  { type: EarningsFilterType.ACTIVE, label: 'Active' },
  { type: EarningsFilterType.CLAIMED, label: 'Claimed' },
  { type: EarningsFilterType.ALL, label: 'All' },
];

export const statusConfig: Record<EarningsTableStatus, { color: string }> = {
  [EarningsTableStatus.CLAIMED]: { color: '#20AA67' },
  [EarningsTableStatus.FINISHED]: { color: '#4E4E4E' },
  [EarningsTableStatus.DAYS_LEFT]: { color: '#00BFBF' },
  [EarningsTableStatus.FLEXIBLE]: { color: '#FF8000' },
  [EarningsTableStatus.WITHDRAWN]: { color: '#4E4E4E' },
};
